import { gainToDb } from './decibels';
import { LevelMeter } from './meter';

/**
 * Meter report layout: `[peak × channels, rms × channels, clipped (0|1), quanta]`.
 * Reports are Float64Arrays because the quanta counter must stay exact for the
 * length of a broadcast: a Float32 stops counting single steps at 2^24 quanta,
 * about 12 hours at 48 kHz.
 */
export function meterReportLength(channels: number): number {
  return channels * 2 + 2;
}

/**
 * The audio-thread half of a level meter: accumulates blocks, counts render
 * quanta, and hands out a report whenever a hop's worth of frames is in.
 * The quanta counter is cumulative for the life of the object and advances on
 * every `process` call, with or without input, so it measures whether the
 * graph is being pulled rather than whether there is signal.
 */
export class MeterReporter {
  private readonly meter: LevelMeter;
  private readonly report: Float64Array;
  private quanta = 0;

  constructor(
    channels: number,
    private readonly hopFrames: number,
    segments: number,
  ) {
    this.meter = new LevelMeter(channels, segments);
    this.report = new Float64Array(meterReportLength(channels));
  }

  /** Feed one render quantum. Returns the report (one reused array) when a hop completes, else null. */
  process(inputs: readonly Float32Array[], frames: number): Float64Array | null {
    this.quanta++;
    this.meter.push(inputs, frames);
    if (this.meter.pendingFrames < this.hopFrames) return null;
    this.meter.snapshot(this.report);
    this.report[this.report.length - 1] = this.quanta;
    return this.report;
  }
}

/** Write a report's levels into `reading` as dBFS; returns whether the hop clipped. */
export function applyMeterLevels(
  report: ArrayLike<number>,
  channels: number,
  reading: { peakDb: number[]; rmsDb: number[] },
): boolean {
  for (let c = 0; c < channels; c++) {
    reading.peakDb[c] = gainToDb(report[c]);
    reading.rmsDb[c] = gainToDb(report[channels + c]);
  }
  return report[channels * 2] > 0;
}

/** Cumulative render quanta the reporting meter had processed when it sent `report`. */
export function meterReportQuanta(report: ArrayLike<number>): number {
  return report[report.length - 1];
}

/** Web Audio's render quantum; the hold below converts time to quanta with it. */
const RENDER_QUANTUM_FRAMES = 128;

/**
 * Clip indicator that stays on for a fixed stretch of audio time after the
 * last clipping report. Time is taken from the reports' own quanta counter, so
 * the result is the same for every reader and needs no timer.
 */
export class ClipHold {
  private readonly holdQuanta: number;
  private until = 0;

  constructor(holdMs: number, sampleRate: number) {
    this.holdQuanta = Math.ceil((holdMs * sampleRate) / 1000 / RENDER_QUANTUM_FRAMES);
  }

  /** Feed one report; returns whether the indicator is on as of that report. */
  update(quanta: number, clipped: boolean): boolean {
    if (clipped) this.until = quanta + this.holdQuanta;
    return quanta < this.until;
  }

  reset(): void {
    this.until = 0;
  }
}
